import { IInstallment } from "api/interfaces";
import { makeAutoObservable } from "mobx";
import DiscountController from "../api/controllers/DiscountController";
import DiscountModel from "../api/models/DiscountModel";
import DiscountService from "../api/services/DiscountService";
import InstallmentService from "../api/services/InstallmentService";
import { lang } from "../i18nLang";
import { ContractStore, MeasurementListStore, MessageStore } from "./";

import { EDiscountType } from "api/enums";
import { GreaterThanOrEqualException } from "../api/exceptions/InvalidValueException";
import { decimal } from "../utils/DecimalPrecision";

class Store {
  private observer: any = undefined;
  private _discounts: DiscountModel[] = [];
  private _currentDiscount: DiscountModel | undefined = undefined;
  public readonly validations = {
    title: "required",
    date: "date",
    total: { required: true, greaterThan: { valueToCompare: 0 } },
    quantity: { required: true, between: { min: 1, max: 50 } },
  };

  constructor() {
    makeAutoObservable(this);
  }

  get currentDiscount() {
    return this._currentDiscount;
  }

  get discounts() {
    return [...this._discounts];
  }

  get discountsTotal() {
    return decimal(0)
      .sum(...this._discounts.map((discount) => discount.total))
      .toNumber();
  }

  get discountsTotalToBill() {
    return decimal(0)
      .sum(
        ...this._discounts.map((discount) =>
          decimal(discount.total).subtract(discount.totalLinked).toNumber()
        )
      )
      .toNumber();
  }

  private reset() {
    this.observer = undefined;
    this._discounts = [];
    this._currentDiscount = undefined;
  }

  addDiscountsObserver() {
    this.removeObserver();
    this.observer = DiscountService.subscribeDiscountsSnapshot(
      ContractStore.getCurrentContract()?.uid || "",
      (discountsAdded) => this.addDiscountsToList(discountsAdded),
      (discountsChanged) => this.changeDiscountsOnList(discountsChanged),
      (discountsRemoved) => this.removeDiscountsFromList(discountsRemoved)
    );
  }

  private addDiscountsToList(discountsAdded: DiscountModel[]) {
    this._discounts = discountsAdded
      .concat(this._discounts)
      .sort((a, b) => b.code - a.code);
  }

  private changeDiscountsOnList(discountsChanged: DiscountModel[]) {
    const discounts = this._discounts;
    discountsChanged.forEach((disc) => {
      const index = discounts.findIndex((d) => d.uid === disc.uid);
      if (index !== -1) discounts[index] = disc;
    });
    this._discounts = discounts.sort((a, b) => b.code - a.code);
  }

  private removeDiscountsFromList(discountsRemoved: DiscountModel[]) {
    const uids = discountsRemoved.map((disc) => disc.uid);
    this._discounts = this._discounts.filter(
      (disc) => !uids.includes(disc.uid)
    );
  }

  removeObserver() {
    if (this.observer) {
      this.observer();
    }
    this.reset();
  }

  clearCurrentDiscount() {
    this._currentDiscount = undefined;
  }

  setCurrentDiscount(uid: string) {
    this._currentDiscount = this._discounts.find(
      (discount) => discount.uid === uid
    );
  }

  createDiscount(data) {
    DiscountController.createDiscount(
      ContractStore.getCurrentContract()?.uid || "",
      {
        ...data,
        code: this.getNextCode(),
      }
    )
      .then(() => {
        MessageStore.success(lang("discounts.success.created"));
      })
      .catch((error) => {
        console.error(error);
        MessageStore.error(error.messages?.long || error.message);
      });
  }

  private getNextCode(): number {
    let maxCode = 0;
    this._discounts.forEach((discount) => {
      if (discount.code > maxCode) maxCode = discount.code;
    });
    return maxCode + 1;
  }

  public deleteDiscount() {
    if (!this._currentDiscount) return;
    DiscountService.deleteDiscount(this._currentDiscount)
      .then(() => {
        this.removeDiscountsFromList([this._currentDiscount!]);
        MessageStore.success(lang("discounts.success.deleted"));
      })
      .catch((error) => {
        console.error(error);
        MessageStore.error(error.message);
      });
  }

  async updateDiscount(updatedData: {
    title: string;
    description: string;
    total: number;
    date: Date;
    type: EDiscountType;
    quantity: number;
    installments: IInstallment[];
  }) {
    if (!this._currentDiscount) return;

    try {
      const changedTypes = this._currentDiscount.type !== updatedData.type;

      await DiscountController.updateDiscount(
        this._currentDiscount,
        updatedData
      );

      if (changedTypes) {
        await MeasurementListStore.updateMeasurementsDiscountsType(
          this._currentDiscount.uid,
          updatedData.type
        );
      }

      MessageStore.success(lang("discounts.success.updated"));
    } catch (error: any) {
      console.error(error);
      if (error instanceof GreaterThanOrEqualException) {
        if (error.field === "total") {
          MessageStore.error(lang("discounts.error.onUpdateTotal"));
          return;
        }
        if (error.field === "quantity") {
          MessageStore.error(lang("discounts.error.onUpdateQuantity"));
          return;
        }
      }
      MessageStore.error(error.messages?.long || error.message);
    }
  }

  updateInstallmentMaturityDate(installmentId: number, newDate: Date) {
    const discount = this._currentDiscount;
    if (!discount) return;

    discount.setInstallmentMaturityDate(installmentId, newDate);
    this.updateDiscountInstallments(discount);
  }

  createInstallments(
    { total, date, quantity },
    recalculate: boolean = false
  ): IInstallment[] {
    if (!recalculate)
      return InstallmentService.generateInstallments(date, quantity, total);

    if (this._currentDiscount)
      return InstallmentService.recalculateInstallments(
        this._currentDiscount,
        quantity,
        total,
        date
      );

    return [];
  }

  private updateDiscountInstallments(discount: DiscountModel) {
    if (!discount) return;

    DiscountService.updateInstallments(discount).catch((error) => {
      console.error(error);
      MessageStore.error(error.message.long || error.message);
    });
  }

  findDiscount(id: string): DiscountModel | undefined {
    return this._discounts.find((discount) => discount.uid === id);
  }
}

const DiscountStore = new Store();
export default DiscountStore;
